import { createClients, type Session } from '@cybersailor/slauth-ts'

const storageKey = 'carthooks.slauth.session'
let currentAccessToken: string | null = null
let onSessionRefreshedHandler: ((session: Session | null) => void) | null = null
let onUnauthorizedHandler: (() => Promise<void>) | null = null

const clients = createClients({
  auth: {
    url: '/api/auth/slauth',
  },
  autoRefreshToken: true,
  persistSession: true,
  storageKey,
  onSessionRefreshed: (session) => {
    rememberSession(session)
    onSessionRefreshedHandler?.(session)
  },
  onUnauthorized: async () => {
    await onUnauthorizedHandler?.()
  },
})

function getAuthClient() {
  if (!clients.authClient) {
    throw new Error('slauth auth client not initialized')
  }
  return clients.authClient
}

function rememberSession(session: Session | null | undefined) {
  currentAccessToken = session?.access_token || null
}

export function setSlauthRuntimeHandlers(handlers: {
  onSessionRefreshed?: (session: Session | null) => void
  onUnauthorized?: () => Promise<void>
}) {
  onSessionRefreshedHandler = handlers.onSessionRefreshed || null
  onUnauthorizedHandler = handlers.onUnauthorized || null
}

export function getSlauthAuthClient() {
  return getAuthClient()
}

export function getSlauthStorageKey() {
  return storageKey
}

export async function getSlauthSession() {
  const session = await getAuthClient().getSession()
  rememberSession(session)
  return session
}

export async function ingestSlauthSession(session: Session | null | undefined) {
  if (!session?.access_token) return
  await clients.sessionManager.setSession(session)
  rememberSession(session)
  onSessionRefreshedHandler?.(session)
}

export async function clearSlauthSession() {
  await clients.sessionManager.clearSession()
  rememberSession(null)
  onSessionRefreshedHandler?.(null)
}

export async function restoreSlauthSession() {
  const session = await getSlauthSession()
  if (!session?.access_token) return null
  rememberSession(session)
  return session
}

export async function refreshSlauthSession() {
  const data = await getAuthClient().refreshSession()
  const session = data.session || await getSlauthSession()
  if (session?.access_token) {
    rememberSession(session)
    onSessionRefreshedHandler?.(session)
  } else {
    rememberSession(null)
    onSessionRefreshedHandler?.(null)
  }
  return session
}
